import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GroupRole } from '@/__generated__/graphql';
import Alert from '@/components/Alert';
import AppNotifications from '@/components/AppNotifications';
import Button from '@/components/Button';
import ConsultationsStarting from '@/components/ConsultationsStarting';
import FAIcon from '@/components/Icon/FAIcon';
import Link from '@/components/Link';
import { isReviewable } from '@/consultation/reviewWindow';
import { canReviewConsultation } from '@/consultation/reviewer';
import { clearCollection, fetchAwaiting, fetchConfirmed } from '@/consultation/store';
import RedirectCompleteness from '@/profile/components/ProfileCompleteness/RedirectCompleteness';
import { useAppDispatch, useAppSelector } from '@/store';
import { darkGreen } from '@/theme/colors';

import { AccountOverviewCard } from './AccountOverviewCard';
import { CollapsibleSection } from './CollapsibleSection';
import { DashboardCard } from './DashboardCard';
import { DashboardSideNav } from './DashboardSideNav';
import ExpertApplicationPromo from './ExpertApplicationPromo';
import { MyActivityCard } from './MyActivityCard';
import { MyCandidaciesCard } from './MyCandidaciesCard';
import { MyExpertsCard } from './MyExpertsCard';
import { MyProjectsCard } from './MyProjectsCard';
import NoConsultationsInstructions from './NoConsultationsInstructions';
import { OrgExpertNetworkCard } from './OrgExpertNetworkCard';
import { PersonalReviewBanner } from './PersonalReviewBanner';
import { RecentConsultationsStrip } from './RecentConsultationsStrip';
import { TeamActivityCard } from './TeamActivityCard';
import { UpcomingCallsCard } from './UpcomingCallsCard';
import { CLIENT_SECTIONS, EXPERT_SECTIONS, SCOPE_LABELS, TEAM_SECTIONS } from './dashboardSections';
import {
  useCostByProjectCents,
  useDashboardConsultations,
  useDashboardContext,
  useDashboardExpertsConsultedCount,
  useDashboardProjects,
  useDashboardProjectsTotal,
  useProjectStats,
  useScheduledByExpert,
  useUnreviewedCount,
  useYourExperts,
} from './useDashboardData';
import { useDashboardTeamMembers } from './useDashboardTeamMembers';
import { useSectionObserver } from './useSectionObserver';
import { useViewerGroupRole } from './useViewerGroupRole';

// Regular dashboard body — runs all data hooks. Extracted from DashboardPage
// so the SMB-incomplete early-return in the parent doesn't violate Rules of
// Hooks (see ENG-5251 plan).
export function DashboardBody(): React.ReactNode {
  const viewer = useAppSelector((state) => state.viewer);
  // 'admin' is a real landing context here, not one to rewrite to a team: an
  // OnFrontiers admin logs in to it (getDefaultUserContext) and it renders as their
  // personal layout, whose cards the api scopes to their own consultations. ENG-5827.
  const userContext = useAppSelector((state) => state.ui.userContext);
  const awaitingConsultations = useAppSelector(
    (state) => state.consultations.dashboardAwaiting || {}
  );
  const confirmedConsultations = useAppSelector(
    (state) => state.consultations.dashboardConfirmed || {}
  );

  const dispatch = useAppDispatch();

  // Re-scope the Upcoming Calls collections when the active company changes. The
  // route action seeds them once at load; an in-session "View as" switch must
  // refetch so the calls match the selected company. The clearCollection calls
  // reset each collection so fetchConsultations' shouldResetCollection guard lets
  // the refetch through, independent of the consultation reducer's own reset on
  // UI__SET_USER_CONTEXT. ENG-5254.
  const contextInitialized = useRef(false);
  useEffect(() => {
    if (!contextInitialized.current) {
      contextInitialized.current = true;
      return;
    }
    dispatch(clearCollection('dashboardAwaiting'));
    dispatch(clearCollection('dashboardConfirmed'));
    dispatch(fetchAwaiting('', 1000, false, 'dashboardAwaiting'));
    dispatch(fetchConfirmed('', 1000, false, 'dashboardConfirmed'));
  }, [userContext, dispatch]);

  const { dashCtx, groupId, billingAccountId, group, classifying, classifyError, isStaffViewer } =
    useDashboardContext();
  const isExpert = userContext === 'expert';
  const isCreditTeam = dashCtx === 'credit-team';
  const isSMBTeam = dashCtx === 'smb-team';

  // Data hooks
  const {
    consultations: recentConsultations,
    loading: consultationsLoading,
    error: consultationsError,
  } = useDashboardConsultations(userContext);
  const {
    projects,
    loading: projectsLoading,
    error: projectsError,
  } = useDashboardProjects(groupId, !isStaffViewer);
  const projectsTotal = useDashboardProjectsTotal(groupId);
  // Team Activity and the Account scope are shown to Admins/Owners — and to platform
  // staff viewing a company they don't belong to, so a staff viewer lands on the full
  // Account view instead of the My-activity-only scope (ENG-5257 / ENG-5256 / ENG-5254).
  const { role } = useViewerGroupRole(isCreditTeam || isSMBTeam ? groupId : undefined);
  const canSeeTeamActivity = role === GroupRole.Admin || role === GroupRole.Owner || isStaffViewer;
  const canSeeAccountScope = role === GroupRole.Admin || role === GroupRole.Owner || isStaffViewer;
  const {
    members: teamMembers,
    loading: teamMembersLoading,
    error: teamMembersError,
  } = useDashboardTeamMembers(isCreditTeam || isSMBTeam ? groupId : undefined, canSeeTeamActivity);

  // Derived data
  const scheduledByExpert = useScheduledByExpert(userContext);
  const yourExperts = useYourExperts(recentConsultations, scheduledByExpert, dashCtx, viewer.id);
  const expertsConsultedCount = useDashboardExpertsConsultedCount(
    isCreditTeam || isSMBTeam ? userContext : ''
  );
  const costByProjectCents = useCostByProjectCents(recentConsultations, dashCtx);
  const { attentionCount } = useProjectStats(projects);
  const unreviewedCount = useUnreviewedCount(recentConsultations, viewer.id);
  // ENG-5256: personal review banner uses the viewer-scoped unreviewedConsultations
  // true count (Redux), not the capped useUnreviewedCount(recentConsultations).
  // ENG-5255: only count consultations still inside the 30-day review window.
  const personalUnreviewedEdges = useAppSelector((state) => state.consultations.unreviewed?.edges);
  const personalUnreviewedCount = useMemo(
    () =>
      (personalUnreviewedEdges ?? []).filter(
        (e) => isReviewable(e?.node?.starts_at) && canReviewConsultation(viewer.id, e?.node)
      ).length,
    [personalUnreviewedEdges, viewer.id]
  );

  const isTeam = isCreditTeam || isSMBTeam;

  // Side nav: the Team layout is split into two role-gated scopes. Owners/Admins
  // see both Account and My-activity sections; Members/Viewers see only My
  // activity (the Account sections are filtered out entirely).
  const sections = useMemo(() => {
    if (isTeam) {
      return canSeeAccountScope
        ? TEAM_SECTIONS
        : TEAM_SECTIONS.filter((s) => s.scope === 'activity');
    }
    if (isExpert) return EXPERT_SECTIONS;
    return CLIENT_SECTIONS;
  }, [isTeam, isExpert, canSeeAccountScope]);

  // ENG-5298: Account and My activity render stacked on one scrollable page, so
  // the observer tracks every rendered section. `sections` is already
  // role-filtered, so Members/Viewers still observe only the activity IDs.
  const sectionIds = useMemo(() => sections.map((s) => s.id), [sections]);
  const activeSection = useSectionObserver(sectionIds);

  const sideNavBadges = useMemo(
    () => ({
      // Unreviewed count is a property of consultations (pending reviews), so it
      // badges the Consultations section — matching the review banner and the
      // Leave-a-Review CTAs that live there — not Experts.
      'recent-consultations': unreviewedCount > 0 ? unreviewedCount : undefined,
      'my-projects': attentionCount > 0 ? attentionCount : undefined,
    }),
    [unreviewedCount, attentionCount]
  );

  // group is resolved by useDashboardContext, which works for a company a
  // staff/admin views but isn't a member of. ENG-5254.
  const planType = group?.capabilities?.planType ?? null;

  const [passwordUpdateClosed, setPasswordUpdate] = useState(false);
  const [profileCompleted, setProfileCompleted] = useState(false);
  const handlePasswordUpdateClose = useCallback(() => setPasswordUpdate(true), []);
  const handleProfileCompleted = useCallback((b: boolean) => setProfileCompleted(b), []);

  const isApplying =
    viewer.expert_state === 'applying' || (viewer.signup_type === 'expert' && !viewer.expert_state);

  const loading = awaitingConsultations.loading || confirmedConsultations.loading;
  const totalConsultations =
    (awaitingConsultations.edges || []).length + (confirmedConsultations.edges || []).length;

  // ENG-5570: the "Review & Accept" agreement banners that used to live here
  // are gone. A gated expert is redirected to /legal/accept before any
  // authenticated route renders, so a dashboard prompting them to accept was
  // unreachable by construction.
  const alerts = [];

  if (!passwordUpdateClosed && viewer.password_expiry && viewer.password_expiry.expiring) {
    alerts.push(
      <Alert
        icon={<FAIcon color={darkGreen} icon="lock" />}
        preset="notification"
        onRequestClose={handlePasswordUpdateClose}
        key="password-update-alert"
      >
        <div>
          To increase security, we ask users to update their password periodically. Your password
          will expire at {moment(viewer.password_expiry.expiry).format('MMM DD')}.
          <br />
          <br />
          <Link to="/settings/password">
            <Button variant="contained" size="medium">
              Update Password
            </Button>
          </Link>
        </div>
      </Alert>
    );
  }

  return (
    <div id="dashboard-root">
      <ConsultationsStarting />
      {alerts}
      <AppNotifications />
      <RedirectCompleteness profile={viewer.profile} onProfileCompleted={handleProfileCompleted} />

      <div className="flex flex-col gap-16 lg:flex-row lg:gap-24">
        <DashboardSideNav
          sections={sections}
          activeSection={activeSection}
          badges={sideNavBadges}
        />

        <div className="min-w-0 flex-1">
          {/* Team Layout (credit-team and SMB share the same shape; AccountOverviewCard
              self-sources its date range, range-scoped group stats, and credit
              balance, and swaps the SMB vs credit header link/credits row.
              ENG-5298: the two scopes render stacked on one scrollable page —
              Account (Owners/Admins/staff only) on top, My activity below for
              every team user. The role gate renders from the last-known role:
              a cold cache fails closed to My activity until the role query
              resolves; a cached role (cache-and-network) renders immediately
              while revalidating, avoiding an every-visit layout shift. */}
          {isTeam && canSeeAccountScope && (
            <>
              <h2 className="sr-only">{SCOPE_LABELS.account}</h2>
              <section
                id="account-overview"
                tabIndex={-1}
                data-screen-label="Account Overview"
                className="scroll-mt-80"
              >
                <AccountOverviewCard
                  groupId={groupId}
                  billingAccountId={billingAccountId}
                  companyName={group?.name ?? ''}
                  planType={planType}
                  groupSlug={group?.slug ?? ''}
                  isSMB={isSMBTeam}
                />
              </section>
              {group?.internal_network?.id && (
                <div className="mt-20">
                  <CollapsibleSection id="expert-network" data-screen-label="Network">
                    {({ collapsed, collapseButton }) => (
                      <OrgExpertNetworkCard
                        internalNetworkId={group.internal_network!.id}
                        collapseButton={collapseButton}
                        collapsed={collapsed}
                      />
                    )}
                  </CollapsibleSection>
                </div>
              )}
              {/* Team Activity (#2699) is Owner/Admin-only — same gate as the
                  Account scope. ENG-5256 gives it its own `team-activity` nav
                  item under Account (after Network); the CollapsibleSection
                  renders the matching section anchor the observer tracks. */}
              {canSeeTeamActivity && (
                <div className="mt-20">
                  <CollapsibleSection id="team-activity" data-screen-label="Team Activity">
                    {({ collapsed, collapseButton }) => (
                      <TeamActivityCard
                        members={teamMembers}
                        loading={teamMembersLoading}
                        error={!!teamMembersError}
                        groupSlug={group?.slug ?? ''}
                        collapseButton={collapseButton}
                        collapsed={collapsed}
                      />
                    )}
                  </CollapsibleSection>
                </div>
              )}
            </>
          )}

          {isTeam && (
            <>
              <h2 className="sr-only">{SCOPE_LABELS.activity}</h2>
              {/* Seam: standard inter-section gap when the Account block sits above. */}
              <div className={canSeeAccountScope ? 'mt-20' : undefined}>
                <CollapsibleSection id="upcoming-calls" data-screen-label="Upcoming Calls">
                  {({ collapsed, collapseButton }) => (
                    <UpcomingCallsCard
                      fullWidth
                      title="My Upcoming Calls"
                      collapsed={collapsed}
                      collapseButton={collapseButton}
                    />
                  )}
                </CollapsibleSection>
              </div>
              <div className="mt-20">
                <CollapsibleSection id="my-projects" data-screen-label="Projects">
                  {({ collapsed, collapseButton }) => (
                    <MyProjectsCard
                      projects={projects}
                      total={projectsTotal}
                      costByProjectCents={costByProjectCents}
                      dashCtx={dashCtx}
                      attentionCount={attentionCount}
                      loading={projectsLoading}
                      error={!!projectsError}
                      collapseButton={collapseButton}
                      collapsed={collapsed}
                    />
                  )}
                </CollapsibleSection>
              </div>
              <div className="mt-20">
                <CollapsibleSection id="recent-consultations" data-screen-label="Consultations">
                  {({ collapsed, collapseButton }) => (
                    <>
                      <PersonalReviewBanner count={personalUnreviewedCount} />
                      <RecentConsultationsStrip
                        consultations={recentConsultations}
                        loading={consultationsLoading}
                        error={!!consultationsError}
                        title="My Consultations"
                        layout="vertical"
                        dashCtx={dashCtx}
                        collapseButton={collapseButton}
                        collapsed={collapsed}
                      />
                    </>
                  )}
                </CollapsibleSection>
              </div>
              <div className="mt-20">
                <CollapsibleSection id="my-experts" data-screen-label="Experts">
                  {({ collapsed, collapseButton }) => (
                    <MyExpertsCard
                      experts={yourExperts}
                      total={expertsConsultedCount}
                      unreviewedCount={unreviewedCount}
                      dashCtx={dashCtx}
                      loading={consultationsLoading}
                      error={!!consultationsError}
                      internalNetworkId={group?.internal_network?.id}
                      collapseButton={collapseButton}
                      collapsed={collapsed}
                    />
                  )}
                </CollapsibleSection>
              </div>
            </>
          )}

          {/* Expert Layout */}
          {isExpert && (
            <>
              <section id="my-activity" tabIndex={-1} className="scroll-mt-80">
                <div className="grid grid-cols-2 gap-16 max-md:grid-cols-1">
                  <MyActivityCard />
                  <UpcomingCallsCard />
                </div>
              </section>
              <section id="my-engagements" tabIndex={-1} className="mt-16 scroll-mt-80">
                <MyCandidaciesCard />
              </section>
            </>
          )}

          {/* While a non-member company's classification resolves, show a
              placeholder rather than briefly flashing the client layout. ENG-5254. */}
          {classifying && (
            <section id="account-overview" className="scroll-mt-80">
              <DashboardCard title="Account overview" loading fullWidth>
                {null}
              </DashboardCard>
            </section>
          )}

          {/* Classification fetch failed (e.g. staff viewing a company): surface an
              error instead of silently falling back to the client layout — the exact
              symptom this ticket fixes. ENG-5254. */}
          {classifyError && (
            <section id="account-overview" className="scroll-mt-80">
              <DashboardCard title="Account overview" error fullWidth>
                {null}
              </DashboardCard>
            </section>
          )}

          {/* Client (no billing) Layout */}
          {!isCreditTeam && !isSMBTeam && !isExpert && !classifying && !classifyError && (
            <>
              <section id="upcoming-calls" tabIndex={-1} className="scroll-mt-80">
                <UpcomingCallsCard fullWidth />
              </section>
              <section id="recent-consultations" tabIndex={-1} className="mt-16 scroll-mt-80">
                <RecentConsultationsStrip
                  consultations={recentConsultations}
                  loading={consultationsLoading}
                  error={!!consultationsError}
                  dashCtx={dashCtx}
                />
              </section>
            </>
          )}

          {isExpert && isApplying && (
            <div className="mt-16">
              <ExpertApplicationPromo profileCompleted={profileCompleted} />
            </div>
          )}
          {isExpert && !isApplying && !loading && totalConsultations === 0 && (
            <div className="mt-16">
              <NoConsultationsInstructions />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
